import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import { useQueryClient } from "@tanstack/react-query";

import { useGetOperationStatus } from "@/api/dataset/useDatasetAPI";
import NotificationContent from "@/components/notification/notificationContent";
import SaveSuccessNotiContent from "@/components/spatialAnalysis/notification/saveConfigurationSuccess";
import type { DatasetEntry } from "@/components/spatialAnalysis/saveAsDatasetPopup";
import { MESSAGE } from "@/constants/message";
import useToast, { EToastType } from "@/hooks/ui/useToast";
import { OperationStatusType } from "@/types/dataset";

const POLLING_INTERVAL_MS = 5000;
/* Counted with `errorUpdateCount`, not `fetchFailureCount`: React Query resets
   the latter at the start of every fetch, so with the app-wide `retry: 0` it
   never climbs above 1 and a threshold on it can never be reached. */
export const MAX_POLL_FAILURE = 3;

export type UseSaveAnalysisDatasetStatusParams = {
  /** Called once the operation succeeds, with the datasets it saved. */
  onSuccess: (savedDatasets: DatasetEntry[]) => void;
  onOpenDataExplorer: () => void;
};

type PendingOperation = {
  id: string;
  savedDatasets: DatasetEntry[];
};

/** Polls a SAVE_ANALYSIS_RESULT operation and owns its terminal toasts,
 * mirroring the synchronous v1 save UX. */
export const useSaveAnalysisDatasetStatus = ({
  onSuccess,
  onOpenDataExplorer
}: UseSaveAnalysisDatasetStatusParams) => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  const [operation, setOperation] = useState<PendingOperation | null>(null);
  /** Whether a "saving..." toast is still waiting on a poll to resolve it. */
  const isPollingRef = useRef(false);

  const settle = () => {
    isPollingRef.current = false;
    setOperation(null);
    toast.dismiss();
  };

  const { data, errorUpdateCount } = useGetOperationStatus(
    operation?.id ?? "",
    {
      enabled: !!operation,
      // Pure scheduler: the toasts live in the effects below, so React Query is
      // free to recompute this as often as it likes.
      refetchInterval: (query) => {
        if (query.state.errorUpdateCount > MAX_POLL_FAILURE) return false;

        const status = query.state.data?.status;
        if (
          status === OperationStatusType.SUCCESS ||
          status === OperationStatusType.FAILED
        )
          return false;

        return POLLING_INTERVAL_MS;
      },
      staleTime: 0,
      gcTime: 0,
      refetchIntervalInBackground: true
    }
  );

  /* Resets rather than refetches: `errorUpdateCount` never decreases, so a plain
     refetch would leave polling switched off and strand an operation that comes
     back still PENDING. Resetting zeroes the count and resumes the interval. */
  const handleRetry = () => {
    if (!operation) return;

    isPollingRef.current = true;
    showToast({
      type: EToastType.LOADING,
      message: MESSAGE.SAVING_YOUR_DATASET
    });
    queryClient.resetQueries({
      queryKey: ["operation-status", operation.id]
    });
  };

  /* Callbacks and `showToast` are deliberately excluded: they are re-created
     every render, so including them would re-fire these toasts. */
  useEffect(() => {
    if (!data || !operation) return;

    if (data.status === OperationStatusType.SUCCESS) {
      settle();
      onSuccess(operation.savedDatasets);
      showToast({
        type: EToastType.SUCCESS,
        autoClose: false,
        message: (
          <SaveSuccessNotiContent
            title={MESSAGE.ANALYSIS_SAVE_AS_USER_DATASET_SUCCESS}
            description={
              MESSAGE.ANALYSIS_SAVE_AS_USER_DATASET_SUCCESS_DESCRIPTION
            }
            buttonLabel="Open data explorer"
            onOpen={onOpenDataExplorer}
          />
        )
      });
      return;
    }

    if (data.status === OperationStatusType.FAILED) {
      settle();
      showToast({
        type: EToastType.ERROR,
        autoClose: false,
        message: (
          <NotificationContent
            title={MESSAGE.ANALYSIS_SAVE_AS_USER_DATASET_FAILED_TITLE}
            description={data.message ?? MESSAGE.SAVED_YOUR_DATASET_FAILED}
          />
        )
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, operation]);

  /* Polling has given up (see refetchInterval); offer a manual retry instead of
     leaving the loading toast up forever. */
  useEffect(() => {
    const hasGivenUpPolling = errorUpdateCount > MAX_POLL_FAILURE;
    if (!hasGivenUpPolling) return;

    /* `operation` is kept so "Try again" can re-arm the same id. */
    isPollingRef.current = false;
    toast.dismiss();
    showToast({
      type: EToastType.ERROR,
      autoClose: false,
      message: (
        <NotificationContent description={MESSAGE.SAVED_YOUR_DATASET_FAILED}>
          <button
            className="mt-1 text-royal-blue font-semibold cursor-pointer"
            type="button"
            onClick={handleRetry}
          >
            Try again
          </button>
        </NotificationContent>
      )
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorUpdateCount]);

  /* Polling lives with the Analysis Result panel, so unmounting it (panel
     closed, Edit Input, route change) ends the poll: drop the "saving..." toast
     rather than leaving it spinning with nothing left to resolve it. Keyed on a
     ref, not on `operation`, so settling does not dismiss its own toast. */
  useEffect(
    () => () => {
      if (isPollingRef.current) toast.dismiss();
    },
    []
  );

  /* The caller already raised the "saving..." toast when it fired the request
     (as the v1 flow does), so polling only takes over the terminal toasts. */
  const startPolling = (id: string, savedDatasets: DatasetEntry[]) => {
    isPollingRef.current = true;
    setOperation({ id, savedDatasets });
  };

  return { operationId: operation?.id ?? null, startPolling };
};
